import { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  QueryClient,
  QueryClientContext,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { employeeRightsService, pospRightsService, getErrorMessage } from '@/services';
import {
  ADMIN_ROLES,
  applyPospLocks,
  LEGACY_CODE_LEVEL,
  POSP_ROLES,
  RIGHTS_GATED_ROLES,
  RIGHTS_STALE_EVENT,
  deriveMatrixFromLegacy,
  normalizeMatrix,
  uniformMatrix,
  type BulkSetPOSPRightsResponse,
  type BulkSetRightsResponse,
  type EffectiveRightsResponse,
  type ModuleMatrix,
  type ModuleOverrides,
  type RightsLevel,
  type RightsModule,
  type RightsSubjectType,
} from '@/services/employeeRights';
import { useAuthStore } from '@/store';

/**
 * Roles that bypass the fine-grained rights model entirely (always allowed).
 * Mirrors `ADMIN_ROLES` in apps/brokers/rights_catalog.py.
 *
 * `RIGHTS_GATED_ROLES` (broker_employee, posp, super_posp) resolve their matrix
 * from `/employee-rights/me/`. Every other role (customer, insurer_*, anything
 * added later) has no rights model: role floors govern them, so every check
 * here passes for them -- flipping that would black out screens that work.
 *
 * `useMyRights().enabled` and every `useCan` MUST agree on that list: a role
 * gated here but whose query never fires reads `undefined` data as "denied",
 * i.e. a lockout wearing a permission fix as a disguise.
 */
const ALWAYS_ALLOWED_ROLES = ADMIN_ROLES;

const MY_RIGHTS_KEY = ['employee-rights-me'] as const;

/**
 * Combined Rights Management listing (point 11): every POSP, Advertiser,
 * Employee and Consolidator user for the caller's own broker.
 */
export function useRightsUsers() {
  return useQuery({
    queryKey: ['rights-users'],
    queryFn: () => employeeRightsService.getRightsUsers(),
  });
}

export function useRightsCatalog() {
  return useQuery({
    queryKey: ['employee-rights-catalog'],
    queryFn: () => employeeRightsService.getCatalog(),
    staleTime: 1000 * 60 * 60, // 1 hour -- catalog is essentially static
  });
}

export function useEmployeeRights(employeeId?: string) {
  return useQuery({
    queryKey: ['employee-rights', employeeId],
    queryFn: () => employeeRightsService.list(employeeId),
    enabled: !!employeeId,
  });
}

export function useBulkSetEmployeeRights() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      rights,
    }: {
      employeeId: string;
      rights: Record<string, boolean>;
    }) => employeeRightsService.bulkSet(employeeId, rights),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['employee-rights', variables.employeeId] });
      queryClient.invalidateQueries({ queryKey: ['employee-rights'] });
      // A per-user override can change what "effective" means for that
      // employee -- refresh the self-gating cache too (harmless if it's not
      // the current user, react-query just refetches on next mount).
      queryClient.invalidateQueries({ queryKey: ['employee-rights-me'] });
      toast.success('Rights saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// =============================================================================
// POSP Rights (B6) -- same catalog, own persistence
// =============================================================================

export function usePOSPRights(pospId?: string) {
  return useQuery({
    queryKey: ['posp-rights', pospId],
    queryFn: () => pospRightsService.list(pospId),
    enabled: !!pospId,
  });
}

export function useBulkSetPOSPRights() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pospId, rights }: { pospId: string; rights: Record<string, boolean> }) =>
      pospRightsService.bulkSet(pospId, rights),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posp-rights', variables.pospId] });
      queryClient.invalidateQueries({ queryKey: ['posp-rights'] });
      queryClient.invalidateQueries({ queryKey: ['employee-rights-me'] });
      toast.success('Rights saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// =============================================================================
// Role Right Defaults (per-role defaults)
// =============================================================================

export function useRolesCatalog() {
  return useQuery({
    queryKey: ['employee-rights-roles'],
    queryFn: () => employeeRightsService.getRoles(),
    staleTime: 1000 * 60 * 60,
  });
}

export function useRoleDefaults(role?: string) {
  return useQuery({
    queryKey: ['role-right-defaults', role],
    queryFn: () => employeeRightsService.getRoleDefaults(role),
    enabled: !!role,
  });
}

export function useBulkSetRoleDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ role, rights }: { role: string; rights: Record<string, boolean> }) =>
      employeeRightsService.bulkSetRoleDefaults(role, rights),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['role-right-defaults', variables.role] });
      // Anyone in this department may now have a different effective set.
      queryClient.invalidateQueries({ queryKey: ['employee-rights-me'] });
      toast.success('Role defaults saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// =============================================================================
// B4: module x level rights -- Rights Management matrix
// =============================================================================

export function useRightsModulesCatalog() {
  return useQuery({
    queryKey: ['employee-rights-modules'],
    queryFn: () => employeeRightsService.getModulesCatalog(),
    staleTime: 1000 * 60 * 60, // the vocabulary only changes with a deploy
  });
}

export function useEffectiveRights(subjectType: RightsSubjectType, subjectId?: string) {
  return useQuery({
    queryKey: ['employee-rights-effective', subjectType, subjectId],
    queryFn: () => employeeRightsService.getEffective(subjectType, subjectId!),
    enabled: !!subjectId,
  });
}

/**
 * Save module/level overrides for one employee or POSP. The response carries
 * the subject's new resolved matrix and sources, so it is written straight into
 * the `effective` cache -- the grid shows what the SERVER now holds, not what
 * the form sent.
 */
export function useBulkSetModuleRights() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      subjectType,
      subjectId,
      modules,
    }: {
      subjectType: RightsSubjectType;
      subjectId: string;
      modules: ModuleOverrides;
    }): Promise<BulkSetRightsResponse | BulkSetPOSPRightsResponse> =>
      subjectType === 'employee'
        ? employeeRightsService.bulkSetModules(subjectId, modules)
        : pospRightsService.bulkSetModules(subjectId, modules),
    onSuccess: (data, { subjectType, subjectId }) => {
      if (data?.modules && data?.sources) {
        queryClient.setQueryData<EffectiveRightsResponse>(
          ['employee-rights-effective', subjectType, subjectId],
          {
            subject_type: subjectType,
            subject_id: subjectId,
            modules: data.modules,
            sources: data.sources,
          }
        );
      }
      queryClient.invalidateQueries({ queryKey: ['employee-rights-effective', subjectType, subjectId] });
      queryClient.invalidateQueries({
        queryKey: subjectType === 'employee' ? ['employee-rights'] : ['posp-rights'],
      });
      queryClient.invalidateQueries({ queryKey: MY_RIGHTS_KEY });
      toast.success('Rights saved');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// =============================================================================
// Self-gating: the caller's own EFFECTIVE rights
// =============================================================================

/**
 * A client for a component rendered outside `QueryClientProvider` (an isolated
 * test render). The app always provides one; this only keeps a rights check
 * from throwing where there is none. Its query is disabled for every
 * non-gated role, so it never fetches in those renders.
 */
let fallbackClient: QueryClient | null = null;
function useResolvedQueryClient(): QueryClient {
  const contextClient = useContext(QueryClientContext);
  if (contextClient) return contextClient;
  if (!fallbackClient) fallbackClient = new QueryClient();
  return fallbackClient;
}

/** At most one invalidation per window, however many hooks are listening. */
export const RIGHTS_STALE_THROTTLE_MS = 5_000;
let lastStaleInvalidation = Number.NEGATIVE_INFINITY;

/** Test seam: forget the last 403-driven invalidation. */
export function resetRightsStaleThrottle(): void {
  lastStaleInvalidation = Number.NEGATIVE_INFINITY;
}

function isGatedRole(role: string | undefined): boolean {
  return !!role && RIGHTS_GATED_ROLES.includes(role);
}

/**
 * The current user's effective rights, as the backend resolves them.
 *
 * B4 "must work in real time": a revoke has to reach an open screen without a
 * reload, so the answer is refetched
 *  - every 60 s while the tab is visible (`refetchInterval`),
 *  - when the window regains focus -- overriding App.tsx's global
 *    `refetchOnWindowFocus: false`, which would otherwise win,
 *  - and on any 403 from the API (`policy:rights-stale`, dispatched by the
 *    axios interceptor), at most once every 5 s.
 *
 * The query is disabled for every non-gated role: admins always pass, and
 * customer/insurer roles have no rights model.
 */
export function useMyRights() {
  const { user } = useAuthStore();
  const client = useResolvedQueryClient();
  const enabled = !!user && isGatedRole(user.role);

  const query = useQuery(
    {
      queryKey: MY_RIGHTS_KEY,
      queryFn: () => employeeRightsService.getMyRights(),
      enabled,
      staleTime: 30_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    },
    client
  );

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const onStale = () => {
      const now = Date.now();
      if (now - lastStaleInvalidation < RIGHTS_STALE_THROTTLE_MS) return;
      lastStaleInvalidation = now;
      client.invalidateQueries({ queryKey: MY_RIGHTS_KEY });
    };
    window.addEventListener(RIGHTS_STALE_EVENT, onStale);
    return () => window.removeEventListener(RIGHTS_STALE_EVENT, onStale);
  }, [enabled, client]);

  return query;
}

/**
 * The caller's full 10x4 matrix.
 *
 * - Not logged in: every cell false.
 * - Admin roles and roles outside the rights model: every cell true (the
 *   matrix only ever NARROWS a role check; it never opens one).
 * - Gated roles: `me.modules`, or -- for a backend that has not deployed B4
 *   yet -- a matrix derived from `me.rights` + BASELINE. `undefined` while the
 *   first answer is loading, and `undefined` (not loading) if it failed, which
 *   every consumer treats as denied.
 */
export interface ModuleMatrixState {
  matrix: ModuleMatrix | undefined;
  isLoading: boolean;
  /**
   * The request FAILED and there is no earlier answer to fall back on. Every
   * cell still reads as denied (fail closed), but a screen must say "could not
   * check" rather than "you may not" -- a gunicorn reload or a 5xx is not a
   * revoked right.
   */
  isError: boolean;
  /** Ask again. The 60 s interval and window focus also retry on their own. */
  retry: () => void;
}

export function useModuleMatrix(): ModuleMatrixState {
  const { user } = useAuthStore();
  const { data, isLoading, isError, refetch } = useMyRights();
  const role = user?.role;

  const matrix = useMemo<ModuleMatrix | undefined>(() => {
    if (!role) return uniformMatrix(false);
    if (!isGatedRole(role)) return uniformMatrix(true);
    if (!data) return undefined;
    if (data.modules && typeof data.modules === 'object') {
      const normalized = normalizeMatrix(data.modules);
      // The server already forces these; repeating it costs nothing and keeps
      // a stale or partial payload from drawing a control a POSP can never use.
      return POSP_ROLES.includes(role) ? applyPospLocks(normalized) : normalized;
    }
    return deriveMatrixFromLegacy(role, data.rights);
  }, [role, data]);

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    matrix,
    isLoading: isGatedRole(role) && isLoading,
    // A failed background refetch keeps the last good answer (`data`), which
    // is still the best information there is -- only a failure with nothing
    // to show is an error state.
    isError: isGatedRole(role) && isError && !data,
    retry,
  };
}

/** Whether the caller may act at `level` on `module`, and whether that is still loading. */
export function useCanState(
  module: RightsModule,
  level: RightsLevel
): { allowed: boolean; isLoading: boolean; isError: boolean; retry: () => void } {
  const { matrix, isLoading, isError, retry } = useModuleMatrix();
  return { allowed: matrix?.[module]?.[level] ?? false, isLoading, isError, retry };
}

/**
 * Whether the current user may act at `level` on `module`.
 *
 * - Not logged in -> false.
 * - super_admin / country_admin / broker_admin -> true.
 * - broker_employee / posp / super_posp -> the matrix cell; false while loading
 *   or when the request failed (fail closed, as the API is).
 * - Every other role -> true; callers keep their own role checks.
 *
 * The API enforces the same cell (`ModuleRight`); this only decides whether a
 * control is drawn.
 */
export function useCan(module: RightsModule, level: RightsLevel): boolean {
  return useCanState(module, level).allowed;
}

/**
 * @deprecated Use `useCan(module, level)`. Kept so a call site this batch
 * missed keeps working: `code` resolves through `LEGACY_CODE_LEVEL`.
 */
export function useHasRight(code: string): boolean {
  const { user } = useAuthStore();
  const mapping = LEGACY_CODE_LEVEL[code];
  // Hooks run unconditionally; an unknown code reads a real cell and is then
  // refused below for gated roles.
  const allowed = useCan(mapping?.[0] ?? 'contacts', mapping?.[1] ?? 'read');

  if (!user) return false;
  if (ALWAYS_ALLOWED_ROLES.includes(user.role)) return true;
  if (!isGatedRole(user.role)) return true;
  if (!mapping) return false;
  return allowed;
}
